import { Box, Button, Container, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteUserRequest, getNotificationSettingsRequest } from 'redux/reducers/settings/reducer';
import {
	getNotificationSettings,
	getNotificationSettingsLoader,
} from 'redux/reducers/settings/selectors';
import { DeleteDialog } from 'ui/DeleteDialog';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { THandleAction } from 'services/constants/tableStructureTypes';
import { EditUserDialog } from './EditUserDialog';
import { AssignNotificationTypeDialog } from './AssignNotificationTypeDialog';
import { tableStructure, TNotificationSettingsTableRow } from './tableStructure';
import { getUserInfo } from 'redux/reducers/user/selectors';
import AddUserIcon from '@mui/icons-material/PersonAdd';

const ROWS = Array.from(new Array(4));

export const UserManagement: FC = () => {
	const dispatch = useDispatch();
	const notificationSettings = useSelector(getNotificationSettings);
	const notificationSettingsIsLoading = useSelector(getNotificationSettingsLoader);
	const easTableRows: TNotificationSettingsTableRow[] = useMemo(
		() =>
			(notificationSettings ?? []).map((u, idx) => ({
				// `EasTableWithoutFilter` требует `id: number`
				id: idx,
				name: [u.firstName, u.lastName].filter(Boolean).join(' '),
				email: u.email,
				role: u.enabled ? 'Dashboard' : 'Notifications only',
				enabled: u.enabled,
				firstName: u.firstName ?? '',
				lastName: u.lastName ?? '',
			})),
		[notificationSettings],
	);

	const easTableItems = useMemo(
		() => ({
			data: easTableRows,
			totalCount: easTableRows.length,
		}),
		[easTableRows],
	);

	useEffect(() => {
		dispatch(getNotificationSettingsRequest());
	}, [dispatch]);

	const [editSettingDialogIsOpen, setEditSettingDialogIsOpen] = useState(false);
	const closeEditSettingDialog = () => setEditSettingDialogIsOpen(false);
	const openEditSettingDialog = () => setEditSettingDialogIsOpen(true);
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

	const [addNewUserDialogIsOpen, setAddNewUserDialogIsOpen] = useState(false);
	const closeAddNewUserDialog = () => setAddNewUserDialogIsOpen(false);
	const openAddNewUserDialog = () => setAddNewUserDialogIsOpen(true);

	const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);
	const closeDeleteDialog = () => setDeleteDialogIsOpen(false);
	const openDeleteDialog = () => setDeleteDialogIsOpen(true);
	const handleDelete = () => {
		if (!notificationSettings?.[selectedIndex ?? 0]?.username) return;
		dispatch(deleteUserRequest({ userId: notificationSettings?.[selectedIndex ?? 0].username }));
		closeDeleteDialog();
	};

	const [assignNotificationTypeDialogIsOpen, setAssignNotificationTypeDialogIsOpen] =
		useState(false);
	const closeAssignNotificationTypeDialog = () => setAssignNotificationTypeDialogIsOpen(false);
	const openAssignNotificationTypeDialog = () => setAssignNotificationTypeDialogIsOpen(true);

	const handleAction: THandleAction = (id, selector) => {
		setSelectedIndex(id);

		switch (selector) {
			case 'Notifications':
				openAssignNotificationTypeDialog();
				break;
			case 'Delete':
				openDeleteDialog();
				break;
			case 'Edit':
				openEditSettingDialog();
				break;
			default:
				break;
		}
	};

	return (
		<Container style={{ padding: '0px 20px 0 20px' }}>
			<EasTableWithoutFilter<TNotificationSettingsTableRow>
				tableStructure={tableStructure}
				items={easTableItems}
				itemsIsLoading={notificationSettingsIsLoading}
				handleAction={handleAction}
				skeletonRows={ROWS}
				highlightedRow={null}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						p: '2px 4px',
					}}
				>
					<Typography sx={{ fontSize: '1.2rem', color: 'primary.main', fontWeight: 'bold' }}>
						Users management
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row' }}>
						<Button
							size="small"
							variant="contained"
							color="primary"
							onClick={openAddNewUserDialog}
							startIcon={<AddUserIcon />}
						>
							Add new user
						</Button>
					</Box>
				</Box>
			</EasTableWithoutFilter>
			<EditUserDialog
				open={editSettingDialogIsOpen}
				setClose={closeEditSettingDialog}
				data={notificationSettings?.[selectedIndex ?? 0] ?? null}
				text="Edit personal data"
				type="edit"
			/>
			<EditUserDialog
				open={addNewUserDialogIsOpen}
				setClose={closeAddNewUserDialog}
				data={null}
				text="Add new user"
				type="add"
			/>

			<DeleteDialog
				open={deleteDialogIsOpen}
				setClose={closeDeleteDialog}
				text="Are you sure you want to delete this user?"
				handleDelete={handleDelete}
			/>

			<AssignNotificationTypeDialog
				open={assignNotificationTypeDialogIsOpen}
				setClose={closeAssignNotificationTypeDialog}
				data={notificationSettings?.[selectedIndex ?? 0] ?? null}
			/>
		</Container>
	);
};
